import { useState } from "react";
import type {
  AddProcedureMaterial,
  EditProcedureMaterial,
  InventoryItem,
  MaterialScope,
  ProcedureCode,
  ProcedureMaterial,
} from "@dentacore/shared-types";

import { describeNumberError, NumberField, sanitiseNumeric } from "../ui/number-field";
import { Pager, useHeight } from "../ui/paging";
import { SCOPES, USES, usesText } from "./stock";
import styles from "./inventory.module.css";

/** A read line and an edit line. Fixed, so a page can be counted. */
const READ_ROW = 46;
const EDIT_ROW = 80;

/**
 * One procedure's tray, opened beside the procedure list — STORY-110b's bill of
 * materials, calm (IV7).
 *
 * **Read first, edited on purpose.** A tray is written once and read on every
 * booking's prep list, so the panel shows it as a plain list — material and
 * amount — and the amount boxes, the per-visit/per-tooth choice, Remove and
 * *Add a material* appear only after *Edit tray*. Every line carried live inputs
 * before, which made reading a tray look like filling in a form.
 *
 * **The scope is the point** (TP15): two restorations in one sitting take one
 * isolation kit but two matrices, so each line says per visit or per tooth.
 */
export function TrayPanel({
  code,
  materials,
  items,
  isAdmin,
  busy,
  editing,
  onEditing,
  onClose,
  onAdd,
  onEdit,
  onRemove,
}: {
  code: ProcedureCode;
  /** Null while it is being read. */
  materials: readonly ProcedureMaterial[] | null;
  /** This location's stock, to choose from when adding. */
  items: readonly InventoryItem[] | null;
  isAdmin: boolean;
  busy: boolean;
  editing: boolean;
  onEditing: (editing: boolean) => void;
  onClose: () => void;
  onAdd: (body: AddProcedureMaterial) => Promise<boolean>;
  onEdit: (materialId: string, body: EditProcedureMaterial) => void;
  onRemove: (materialId: string, name: string) => void;
}): React.JSX.Element {
  const [areaRef, areaHeight] = useHeight<HTMLDivElement>();
  const [page, setPage] = useState(0);

  const edit = editing && isAdmin;
  const lines = materials ?? [];
  const rowHeight = edit ? EDIT_ROW : READ_ROW;
  const pageSize =
    areaHeight <= 0 ? Math.max(1, lines.length) : Math.max(1, Math.floor(areaHeight / rowHeight));
  const count = Math.max(1, Math.ceil(lines.length / pageSize));
  const current = Math.min(page, count - 1);
  const start = current * pageSize;
  const shown = lines.slice(start, start + pageSize);

  return (
    <aside className={styles.panel} aria-label={code.label} data-testid={`materials-${code.id}`}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>{code.label}</h2>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
          data-testid="tray-panel-close"
        >
          ×
        </button>
      </div>
      <p className={styles.panelSub}>What it takes off the shelf</p>

      <div className={styles.trayArea} ref={areaRef}>
        {materials === null ? (
          <p className={styles.blank}>Reading&hellip;</p>
        ) : lines.length === 0 ? (
          <p className={styles.blank}>
            {isAdmin
              ? "Nothing listed yet. Add what a tray for this needs, and every booking will show it."
              : "Nothing is listed for this procedure."}
          </p>
        ) : (
          shown.map((line) =>
            edit ? (
              <EditLine
                key={line.id}
                line={line}
                busy={busy}
                onEdit={(body) => onEdit(line.id, body)}
                onRemove={() => onRemove(line.id, line.itemName)}
              />
            ) : (
              <div key={line.id} className={styles.trayLine}>
                <LineName line={line} />
                <span className={styles.trayAmount}>{amountText(line)}</span>
              </div>
            ),
          )
        )}
      </div>

      {count > 1 && (
        <div className={styles.cardFoot}>
          <Pager
            page={current}
            count={count}
            onPage={setPage}
            from={start + 1}
            to={start + shown.length}
            total={lines.length}
            noun="materials"
          />
        </div>
      )}

      {edit && <AddLine codeId={code.id} lines={lines} items={items} busy={busy} onAdd={onAdd} />}

      {isAdmin && (
        <div className={styles.panelTools}>
          {edit ? (
            <button
              type="button"
              className={styles.primary}
              onClick={() => onEditing(false)}
              data-testid="tray-done"
            >
              Done
            </button>
          ) : (
            <button
              type="button"
              className={styles.ghost}
              onClick={() => onEditing(true)}
              data-testid="tray-edit"
            >
              Edit tray
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

/** "2 uses per tooth", "1 use per visit" — TP15 in the words a person uses. */
export function amountText(line: ProcedureMaterial): string {
  return `${usesText(line.quantity)} ${line.scope === "PER_TOOTH" ? "per tooth" : "per visit"}`;
}

/**
 * The material's name and, when there is one, **one** quiet note under it.
 *
 * *No longer stocked* comes first because it is the one that needs doing
 * something about — kept visible rather than hidden, so a tray naming something
 * the clinic no longer stocks can be corrected.
 */
export function LineName({ line }: { line: ProcedureMaterial }): React.JSX.Element {
  const note = !line.itemIsActive
    ? { text: "No longer stocked", warm: true }
    : line.notes !== null
      ? { text: line.notes, warm: false }
      : line.itemKind === "REUSABLE"
        ? { text: "Never used up", warm: false }
        : null;

  return (
    <span className={styles.lineName}>
      <span className={styles.lineItem}>{line.itemName}</span>
      {note !== null && (
        <span
          className={note.warm ? `${styles.lineNote} ${styles.warmWord}` : styles.lineNote}
          data-testid={note.warm ? "material-retired" : undefined}
        >
          {note.text}
        </span>
      )}
    </span>
  );
}

/**
 * One tray line being edited. **Exported with `AddLine`, `LineName` and
 * `amountText` for the procedure panel** (MC-D1, STORY-175b), which edits the
 * same list in place — one tray, two doors, never two copies of the controls.
 */
export function EditLine({
  line,
  busy,
  onEdit,
  onRemove,
}: {
  line: ProcedureMaterial;
  busy: boolean;
  onEdit: (body: EditProcedureMaterial) => void;
  onRemove: () => void;
}): React.JSX.Element {
  return (
    <div className={styles.editLine}>
      <LineName line={line} />
      {/*
        **Saves as you type, which is why the guard matters** (QA20-13). A
        quantity that does not parse writes nothing — the line keeps what it has
        and the next keystroke that parses replaces it — because "zero composite"
        deducts nothing and prepares nothing.
      */}
      <input
        className={`${styles.input} ${styles.qty}`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={line.quantity}
        disabled={busy}
        aria-label={`How many uses of ${line.itemName}`}
        onChange={(event) => {
          const cleaned = sanitiseNumeric(event.target.value, USES);
          if (describeNumberError(cleaned, USES) !== null || cleaned.trim() === "") return;
          onEdit({ quantity: Number(cleaned), scope: line.scope });
        }}
        data-testid={`material-qty-${line.id}`}
      />
      <select
        className={`${styles.input} ${styles.scope}`}
        value={line.scope}
        disabled={busy}
        aria-label={`How ${line.itemName} is counted`}
        onChange={(event) =>
          onEdit({ quantity: line.quantity, scope: event.target.value as MaterialScope })
        }
        data-testid={`material-scope-${line.id}`}
      >
        {SCOPES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={styles.remove}
        disabled={busy}
        onClick={onRemove}
        aria-label={`Take ${line.itemName} off this tray`}
        title="Remove"
        data-testid={`material-remove-${line.id}`}
      >
        ×
      </button>
    </div>
  );
}

export function AddLine({
  codeId,
  lines,
  items,
  busy,
  onAdd,
}: {
  codeId: string;
  lines: readonly ProcedureMaterial[];
  items: readonly InventoryItem[] | null;
  busy: boolean;
  onAdd: (body: AddProcedureMaterial) => Promise<boolean>;
}): React.JSX.Element | null {
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [scope, setScope] = useState<MaterialScope>("PER_VISIT");

  if (items === null) return null;

  // This location's active stock, minus what is already on the tray. Offering a
  // duplicate would only earn a refusal.
  const already = new Set(lines.map((line) => line.inventoryItemId));
  const addable = items.filter((item) => item.isActive && !already.has(item.id));

  if (items.length === 0) {
    return (
      <p className={styles.panelNote}>
        This location has no stock listed yet, so there is nothing to add here.
      </p>
    );
  }
  if (addable.length === 0) {
    return (
      <p className={styles.panelNote}>Everything this location stocks is already on this list.</p>
    );
  }

  const quantityOk = describeNumberError(quantity, USES) === null && quantity.trim() !== "";

  return (
    <form
      className={styles.addLine}
      onSubmit={(event) => {
        event.preventDefault();
        if (itemId === "" || !quantityOk) return;
        void onAdd({ inventoryItemId: itemId, quantity: Number(quantity), scope }).then(
          (landed) => {
            if (!landed) return;
            setItemId("");
            setQuantity("1");
            setScope("PER_VISIT");
          },
        );
      }}
      data-testid={`material-add-${codeId}`}
    >
      <label className={`${styles.field} ${styles.addItem}`}>
        <span className={styles.fieldLabel}>Add a material</span>
        <select
          className={styles.input}
          value={itemId}
          onChange={(event) => setItemId(event.target.value)}
          data-testid="material-add-item"
        >
          <option value="">Choose from your stock…</option>
          {addable.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </label>
      <NumberField
        label="Uses"
        className={styles.field}
        labelClassName={styles.fieldLabel}
        inputClassName={styles.input}
        value={quantity}
        onChange={setQuantity}
        rules={USES}
        testId="material-add-qty"
      />
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Counted</span>
        <select
          className={styles.input}
          value={scope}
          onChange={(event) => setScope(event.target.value as MaterialScope)}
          data-testid="material-add-scope"
        >
          {SCOPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="submit"
        className={styles.primary}
        disabled={busy || itemId === "" || !quantityOk}
        data-testid="material-add-save"
      >
        Add it
      </button>
    </form>
  );
}
